<template>
  <main class="analyzer">
    <section class="subnav-card">
      <nav class="subnav" aria-label="Image analyzer sections">
        <button
          type="button"
          class="subnav-btn"
          :class="{ 'subnav-btn-active': activeTab === 'upload' }"
          @click="activeTab = 'upload'"
        >
          Colorize Image
        </button>
        <button
          type="button"
          class="subnav-btn"
          :class="{ 'subnav-btn-active': activeTab === 'history' }"
          @click="activeTab = 'history'; getImages();"
        >
          Collection
        </button>
      </nav>
    </section>

    <template v-if="activeTab === 'upload'">
      <section class="card">
        <div class="field">
          <div
            id="image-dropzone"
            class="dropzone"
            :class="{ 'drop-active': isDragActive }"
            @click="openFileDialog"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
            tabindex="0"
            @keydown.enter="openFileDialog"
          >
            <input
              ref="fileInput"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              style="display: none;"
              @change="onFileChange"
            >
            <div v-if="selectedFile" class="dropzone-content">
              <span class="dropzone-filename">
                <i class="fa fa-image" style="margin-right:8px;"></i>
                {{ selectedFile.name }}
              </span>
              <span class="dropzone-change">Click or drag another image to change</span>
            </div>
            <div v-else class="dropzone-content">
              <i class="fa fa-cloud-upload-alt dropzone-icon"></i>
              <span class="dropzone-text">
                Drag &amp; drop your image here, or <span class="link">click to select file</span>
              </span>
              <span class="dropzone-hint">PNG, JPEG, JPG, WEBP</span>
            </div>
          </div>
        </div>

        <div class="actions-row">
          <label class="restore-toggle" for="restore-toggle-input">
            <span class="restore-toggle-label">Fix & Enhance Photo</span>
            <input
              id="restore-toggle-input"
              v-model="restorePhoto"
              class="restore-toggle-input"
              type="checkbox"
            >
            <span class="restore-toggle-track" aria-hidden="true"></span>
          </label>

          <button
            class="analyze-btn"
            :disabled="isSubmitting || !selectedFile"
            @click="analyze"
          >
            {{ isSubmitting ? 'Colorizing...' : 'Colorize Image - 70 Tokens' }}
          </button>
        </div>

        <p v-if="error" class="error">{{ error }}</p>
      </section>

      <!-- If original and generated images exist, show them side by side -->
      <section
        v-if="previewImageUrl"
        class="card result image-pair-section"
      >
        <h2>{{ generatedImageUrl ? 'Colorized Results' : 'Image Preview' }}</h2>
        <div class="image-pair">
          <div class="image-col">
            <span class="image-label">Original</span>
            <img
              :src="previewImageUrl"
              alt="Uploaded original"
              class="side-image side-image--clickable"
              role="button"
              tabindex="0"
              @click="openLightbox(previewImageUrl, 'Uploaded original')"
              @keydown.enter.prevent="openLightbox(previewImageUrl, 'Uploaded original')"
              @keydown.space.prevent="openLightbox(previewImageUrl, 'Uploaded original')"
            >
          </div>
          <div v-if="generatedImageUrl" class="image-col">
            <span class="image-label">Colorized</span>
            <img
              :src="generatedImageUrl"
              alt="Generated colorized result"
              class="side-image side-image--clickable"
              role="button"
              tabindex="0"
              @click="openLightbox(generatedImageUrl, 'Colorized result')"
              @keydown.enter.prevent="openLightbox(generatedImageUrl, 'Colorized result')"
              @keydown.space.prevent="openLightbox(generatedImageUrl, 'Colorized result')"
            >
          </div>
        </div>
        <div>
          <div>
            <button
              v-if="generatedImageUrl"
              class="download-btn"
              @click="downloadGeneratedImage"
              :disabled="!generatedImageUrl"
            >
              <i class="fa fa-download"></i>
              Download
            </button>
          </div>
        </div>
      </section>
    </template>

    <section v-if="activeTab === 'history'" class="card result image-pair-section">
      <div v-if="isLoadingImages" class="history-loading">
        <span class="spinner" aria-hidden="true"></span>
        <p>Loading images...</p>
      </div>
      <div v-else-if="images && images.length" class="history-masonry">
        <div class="history-item" v-for="image in images" :key="image.ID">
          <img
            :src="image.image"
            alt="Saved colorized image"
            class="history-image"
            role="button"
            tabindex="0"
            @click="openLightbox(image.image, 'Saved colorized image')"
            @keydown.enter.prevent="openLightbox(image.image, 'Saved colorized image')"
            @keydown.space.prevent="openLightbox(image.image, 'Saved colorized image')"
          >
          <button
            type="button"
            class="history-image-delete"
            @click="deleteImage(image.ID)"
            aria-label="Delete image"
          >
            <i class="icon icon-trash" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="history-image-download"
            @click="downloadImage(image.image, image.ID)"
            aria-label="Download image"
          >
            Download
          </button>
        </div>
      </div>
      <p v-else class="history-empty">No images found yet.</p>
    </section>

    <Teleport to="body">
      <div
        v-if="lightboxUrl"
        class="image-lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="lightboxAlt || 'Enlarged image'"
        @click="closeLightbox"
      >
        <button
          type="button"
          class="image-lightbox-close"
          aria-label="Close"
          @click.stop="closeLightbox"
        >
          ×
        </button>
        <div class="image-lightbox-inner" @click.stop>
          <img
            :src="lightboxUrl"
            :alt="lightboxAlt"
            class="image-lightbox-img"
          >
        </div>
      </div>
    </Teleport>
  </main>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: 'ImageAnalyzer',
  data() {
    return {
      services: window.services,
      selectedFile: null,
      isSubmitting: false,
      error: null,
      analysisJson: null,
      generatedImageUrl: null,
      isDragActive: false,
      previewImageUrl: null,
      restorePhoto: false,
      activeTab: 'upload',
      images: null,
      isLoadingImages: false,
      lightboxUrl: null,
      lightboxAlt: '',
    };
  },
  created() {
      //this.getImages();
  },
  methods: {
    openLightbox(url, alt) {
      if (!url) return;
      this.lightboxUrl = url;
      this.lightboxAlt = alt || '';
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this.onLightboxKeydown);
    },
    closeLightbox() {
      if (!this.lightboxUrl) return;
      this.lightboxUrl = null;
      this.lightboxAlt = '';
      document.body.style.overflow = '';
      window.removeEventListener('keydown', this.onLightboxKeydown);
    },
    onLightboxKeydown(e) {
      if (e.key === 'Escape') {
        this.closeLightbox();
      }
    },
    openFileDialog() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onDragOver() {
      this.isDragActive = true;
    },
    onDragLeave() {
      this.isDragActive = false;
    },
    onDrop(event) {
      this.isDragActive = false;
      const files = event.dataTransfer.files || [];
      this.handleFileInput(files);
    },
    onFileChange(event) {
      const files = event.target.files || [];
      this.handleFileInput(files);
    },
    handleFileInput(files) {
      this.selectedFile = files.length ? files[0] : null;
      this.error = null;
      this.analysisJson = null;
      this.generatedImageUrl = null;
      this.revokePreviewUrl();
      if (this.selectedFile) {
        this.previewImageUrl = URL.createObjectURL(this.selectedFile);
      } else {
        this.previewImageUrl = null;
      }
    },
    revokePreviewUrl() {
      if (this.previewImageUrl) {
        URL.revokeObjectURL(this.previewImageUrl);
        this.previewImageUrl = null;
      }
    },
    clearSelectedFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async analyze() {
      if (!this.selectedFile) {
        this.error = 'Please choose an image first.';
        return;
      }

      this.isSubmitting = true;
      this.error = null;
      this.analysisJson = null;
      this.generatedImageUrl = null;

      try {
        const response = await this.services.analyzeImage(this.selectedFile, this.restorePhoto);
        const payload = response.data || {};
        this.analysisJson = JSON.stringify(payload.analysis || payload, null, 2);
        // Try both keys for compatibility:
        this.generatedImageUrl = `${window.location.origin}/i/${payload.colorized_image}`;

        if (this.generatedImageUrl) {
          this.clearSelectedFile();
          this.$emit('image-returned');
        }
      } catch (error) {
        const message = error?.response?.data?.error || 'Image analysis failed.';
        this.error = message;
      } finally {
        this.isSubmitting = false;
      }
    },
    downloadGeneratedImage() {
      if (!this.generatedImageUrl) return;
      const link = document.createElement('a');
      link.href = this.generatedImageUrl;
      // Try to keep extension, default to .png
      let filename = this.selectedFile ? this.selectedFile.name.replace(/\.[^/.]+$/, '') + '-colorized.png' : 'colorized.png';
      link.download = filename;
      link.click();
    },
    downloadImage(imageUrl, imageId) {
      if (!imageUrl) return;
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = imageId ? `image-${imageId}.png` : 'image.png';
      link.click();
    },
    deleteImage(imageId) {
      const c = this;

      if (!Array.isArray(this.images)) return;

      Swal.fire({
          title: 'Delete Image',
          text: "Are you sure you want to delete this image?",
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Delete'
      }).then((result) => {
          if (result.value) {

            c.services.deleteImage({id: imageId})
            .then(function () {
              c.images = c.images.filter((image) => image.ID !== imageId);
            });

          }
      });
    },
    getImages() {
        const c = this;
        c.isLoadingImages = true;

        c.services.getImages()
        .then(function (response) {
            c.images = Array.isArray(response.data) ? response.data : [];

            for (let image of c.images) {
              image.image = `${window.location.origin}/i/${image.ID}.jpg`;
            }
        })
        .catch(function () {
            c.images = [];
        })
        .finally(function () {
            c.isLoadingImages = false;
        });
    },
  },
  beforeUnmount() {
    this.closeLightbox();
    this.revokePreviewUrl();
  }
};
</script>

<style scoped>
.analyzer {
  margin: 0 auto;
  max-width: 900px;
  padding: 0 0 40px;
  text-align: left;
}

.card {
  background: rgba(10, 12, 22, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
  margin-bottom: 20px;
  padding: 22px;
  color: #f5f6ff;
}

.subnav-card {
  padding: 0 3px;
  margin-bottom: 20px;
}

.subnav {
  display: flex;
  gap: 26px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0 18px;
}

.subnav-btn {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  color: rgba(245, 246, 255, 0.84);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: -1px;
  padding: 12px 2px 10px;
  transition: color 0.15s, border-color 0.15s;
}

.subnav-btn:hover {
  color: #f5f6ff;
}

.subnav-btn-active {
  border-bottom: 4px solid #b6f65a;
  color: #f5f6ff;
}

.card h1,
.card h2 {
  margin: 0 0 12px;
  text-align: center;
}

.card p {
  color: rgba(245, 246, 255, 0.84);
}

.field {
  margin: 16px 0;
}

.field label {
  display: block;
  color: #f5f6ff;
  font-weight: 600;
  margin-bottom: 8px;
}

/* Drag & Drop Styles */
.dropzone {
  background: rgba(255, 255, 255, 0.06);
  border: 2px dashed #b6f65a;
  border-radius: 10px;
  padding: 38px 16px 28px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  min-height: 120px;
  text-align: center;
  margin-bottom: 10px;
}

.dropzone.drop-active {
  background: rgba(182, 246, 90, 0.12);
  border-color: #98cd1b;
  box-shadow: 0 0 0 2px #a9f24c33;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.dropzone-icon {
  color: #b6f65a;
  font-size: 2.2rem;
  margin-bottom: 7px;
}
.dropzone-text {
  color: #f5f6ff;
  font-size: 1.04rem;
  font-weight: 600;
}
.dropzone-hint {
  color: #b0b2c3;
  font-size: .89rem;
  margin-top: 3px;
}

.link {
  color: #b6f65a;
  text-decoration: underline;
  cursor: pointer;
}

.dropzone-filename {
  color: #ebf7c6;
  background: rgba(182, 246, 90, 0.1);
  padding: 5px 12px 4px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom:4px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.dropzone-change {
  color: #b6f65a;
  font-size: .96em;
  margin-top: 3px;
  text-align: center;
}

.actions-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.restore-toggle {
  align-items: center;
  display: flex;
  gap: 10px;
  user-select: none;
}

.restore-toggle-label {
  color: #f5f6ff;
  font-size: 0.95rem;
  font-weight: 600;
}

.restore-toggle-input {
  cursor: pointer;
  height: 0;
  opacity: 0;
  position: absolute;
  width: 0;
}

.restore-toggle-track {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  display: inline-block;
  height: 22px;
  position: relative;
  transition: background 0.15s ease;
  width: 42px;
}

.restore-toggle-track::after {
  background: #ffffff;
  border-radius: 50%;
  content: '';
  height: 16px;
  left: 3px;
  position: absolute;
  top: 3px;
  transition: transform 0.15s ease;
  width: 16px;
}

.restore-toggle-input:checked + .restore-toggle-track {
  background: #b6f65a;
}

.restore-toggle-input:checked + .restore-toggle-track::after {
  transform: translateX(20px);
}

.restore-toggle-input:focus-visible + .restore-toggle-track {
  box-shadow: 0 0 0 2px rgba(182, 246, 90, 0.45);
}

.analyze-btn {
  background: #b6f65a;
  border: 1px solid #b6f65a;
  border-radius: 999px;
  color: #10141f;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  padding: 11px 18px;
}

.analyze-btn:disabled {
  background: rgba(182, 246, 90, 0.4);
  border-color: rgba(182, 246, 90, 0.4);
  cursor: not-allowed;
  opacity: 0.6;
}

.error {
  color: #ff8c8c;
  font-weight: 600;
  margin-top: 12px;
}

pre {
  background: rgba(4, 6, 12, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: rgba(245, 246, 255, 0.9);
  margin: 0;
  max-height: 500px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Pair images side by side */
.image-pair-section {
  margin-bottom: 22px;
}

.history-loading {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  min-height: 120px;
}

.spinner {
  animation: spin 0.8s linear infinite;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border-top-color: #b6f65a;
  display: inline-block;
  height: 22px;
  width: 22px;
}

.history-empty {
  margin: 10px 0 0;
  text-align: center;
}

.history-masonry {
  column-count: 3;
  column-gap: 14px;
  margin-top: 8px;
}

.history-item {
  break-inside: avoid;
  margin: 0 0 14px;
  position: relative;
}

.history-image {
  border: 1px solid #2f3821;
  border-radius: 10px;
  box-shadow: 0 1px 7px 1px #15240020;
  display: block;
  width: 100%;
  cursor: zoom-in;
}

.history-image:focus-visible {
  outline: 2px solid #b6f65a;
  outline-offset: 2px;
}

.history-image-download {
  align-items: center;
  background: rgba(182, 246, 90, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #10141f;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  height: 34px;
  justify-content: center;
  display: none;
  pointer-events: none;
  position: absolute;
  right: 10px;
  bottom: 10px;
  transform: translateY(5px);
  transition: opacity 0.2s ease, transform 0.2s ease, background 0.15s ease;
  padding: 1px 10px;
}

.history-image-delete {
  align-items: center;
  background: #b6f65a;
  border: 1px solid #b6f65a;
  border-radius: 999px;
  color: #0b0d17;
  cursor: pointer;
  display: none;
  font-size: 14px;
  font-weight: 700;
  height: 34px;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  right: 10px;
  top: 10px;
  transform: translateY(-5px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  width: 34px;
}

.history-image-delete i {
  font-size: 15px;
}

.history-item:hover .history-image-download,
.history-item:focus-within .history-image-download,
.history-item:hover .history-image-delete,
.history-item:focus-within .history-image-delete {
  display: initial;
  pointer-events: auto;
  transform: translateY(0);
}

.history-image-delete:hover,
.history-image-download:hover {
  opacity: .5;
}

@media (max-width: 900px) {
  .history-masonry {
    column-count: 2;
  }
}

@media (max-width: 560px) {
  .history-masonry {
    column-count: 1;
  }
}

.image-pair {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-start;
  margin-top: 18px;
}

.image-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 170px;
  flex: 1 1 230px;
  gap: 10px;
}

.image-label {
  color: #b0b2c3;
  font-size: 1.1em;
  margin-bottom: 7px;
  text-align: center;
}

.side-image {
  border-radius: 10px;
  display: block;
  max-width: 100%;
  max-height: 330px;
  width: auto;
  height: auto;
  background: #23272e;
  border: 1px solid #2f3821;
  box-shadow: 0 1px 7px 1px #15240020;
}

.side-image--clickable {
  cursor: zoom-in;
}

.side-image--clickable:focus-visible {
  outline: 2px solid #b6f65a;
  outline-offset: 3px;
}

.image-lightbox {
  align-items: center;
  background: rgba(5, 6, 13, 0.92);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 48px 16px 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;
  cursor: zoom-out;
}

.image-lightbox-inner {
  cursor: default;
  max-height: calc(100vh - 72px);
  max-width: min(96vw, 1400px);
}

.image-lightbox-img {
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
  display: block;
  max-height: calc(100vh - 72px);
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.image-lightbox-close {
  align-items: center;
  background: rgba(10, 12, 22, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  color: #f5f6ff;
  cursor: pointer;
  display: flex;
  font-size: 28px;
  font-weight: 300;
  height: 44px;
  justify-content: center;
  line-height: 1;
  position: fixed;
  right: 16px;
  top: 16px;
  width: 44px;
  z-index: 10001;
}

.image-lightbox-close:hover {
  background: rgba(182, 246, 90, 0.2);
  border-color: #b6f65a;
  color: #b6f65a;
}

.image-lightbox-close:focus-visible {
  outline: 2px solid #b6f65a;
  outline-offset: 2px;
}

/* Download Button */
.download-btn {
  margin-top: 16px;
  background: #b6f65a;
  border: 1px solid #b6f65a;
  border-radius: 999px;
  color: #10141f;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 8px 16px 8px 16px;
  align-items: center;
  gap: 6px;
  transition: background 0.13s, border 0.13s;
  width: 100%;
  text-align: center;
}
.download-btn:disabled {
  background: rgba(182, 246, 90, 0.4);
  border-color: rgba(182, 246, 90, 0.4);
  cursor: not-allowed;
  opacity: 0.7;
}

.generated-image {
  border-radius: 10px;
  display: block;
  max-width: 100%;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<!-- Add FontAwesome CDN for icons if not in base layout -->
<!--
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.2/css/all.min.css"/>
-->